import { useState, useEffect } from 'react';
import { Lightbulb, ChevronLeft, ChevronRight, X } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_TIPS = [
  { id: 'f-1', category: 'Food Safety', title: 'Temperature is Critical', tip: 'Check & record temperatures of all cooking, holding, and beverage equipment daily.' },
  { id: 'f-2', category: 'Profit', title: 'Labour Target', tip: 'A&W standard: 19% labour cost. Alert threshold: 25%.' },
];

const COLORS = {
  'Food Safety': 'text-red-600 bg-red-50',
  'Guest Experience': 'text-blue-600 bg-blue-50',
  'Speed of Service': 'text-orange-600 bg-orange-50',
  'Operations': 'text-slate-700 bg-slate-100',
  'Cleanliness': 'text-cyan-600 bg-cyan-50',
  'Quality': 'text-amber-700 bg-amber-50',
  'Profit': 'text-green-700 bg-green-50',
  'Team': 'text-indigo-600 bg-indigo-50',
  'Business Brain': 'text-purple-600 bg-purple-50',
};

const DISMISS_KEY = 'newsbar_dismissed_until';

export default function NewsBar() {
  const [tips, setTips] = useState(FALLBACK_TIPS);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const until = localStorage.getItem(DISMISS_KEY);
    if (until && Number(until) > Date.now()) setDismissed(true);
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/best-practices`);
        if (res.data?.items?.length) setTips(res.data.items);
      } catch { /* keep fallback */ }
    })();
  }, []);

  useEffect(() => {
    if (paused || dismissed) return;
    const timer = setInterval(() => setIdx(i => (i + 1) % tips.length), 6000);
    return () => clearInterval(timer);
  }, [paused, dismissed, tips.length]);

  if (dismissed || !tips.length) return null;

  const tip = tips[idx % tips.length];
  const colorClass = COLORS[tip.category] || 'text-slate-600 bg-slate-50';

  const dismiss = () => {
    // Dismiss for 8 hours
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 8 * 60 * 60 * 1000));
    setDismissed(true);
  };

  return (
    <div
      className="bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 border-b border-orange-100 px-3 sm:px-5 py-2"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="news-bar"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-2 sm:gap-3">
        <Lightbulb className="w-4 h-4 text-orange-500 shrink-0" />
        <button
          onClick={() => setIdx(i => (i - 1 + tips.length) % tips.length)}
          className="hidden sm:block text-slate-400 hover:text-orange-500 p-0.5"
          data-testid="news-bar-prev"
        ><ChevronLeft className="w-3.5 h-3.5" /></button>

        <div className="flex-1 min-w-0 flex items-center gap-2 overflow-hidden">
          <span className={`shrink-0 text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${colorClass}`}>
            {tip.category}
          </span>
          <span className="text-xs font-semibold text-slate-800 shrink-0 hidden md:inline">{tip.title}:</span>
          <span className="text-xs text-slate-600 truncate" data-testid="news-bar-tip">
            <span className="md:hidden font-semibold text-slate-800">{tip.title}: </span>
            {tip.tip}
          </span>
        </div>

        <button
          onClick={() => setIdx(i => (i + 1) % tips.length)}
          className="hidden sm:block text-slate-400 hover:text-orange-500 p-0.5"
          data-testid="news-bar-next"
        ><ChevronRight className="w-3.5 h-3.5" /></button>

        <span className="hidden sm:inline text-[10px] text-slate-400 tabular-nums shrink-0">{(idx % tips.length) + 1}/{tips.length}</span>

        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-600 p-0.5 shrink-0"
          title="Hide for 8 hours"
          data-testid="news-bar-dismiss"
        ><X className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}
